// Everything that puts a tournament in the cloud and gets it back out again: the
// user document, the schema-v2 tournament layout, the merge rules that let two
// arbiters score the same event, and the throttled save that carries it all.
//
// Moved here from firebase-auth.js unchanged. It reads the session (who is
// signed in, what their claims say) through session.js and never writes it.
import {
    db,
    doc, setDoc, getDoc, collection, query, where, getDocs, limit, orderBy, deleteDoc, deleteField
} from "./firebase-core.js";
import { osloDate } from "../utils/oslo-date.js";
import { currentUser, currentClaims } from "./session.js";
import { roundsIndexDocId, roundDocId, roundNoOf, buildRoundDocs, readGroupRounds, planRoundsWrite } from "../utils/rounds-split.js";
import { stableStringify } from "../utils/stable-json.js";
import { mustVerifyBeforeEmptying } from "../utils/roster-write-guard.js";
import { splitRoster, mergeRoster, mergePrivateEntries } from "../utils/player-privacy.js";
import { publishedRoundCount } from "../utils/round-publication.js";
import { nsfSeriesInUse } from "../utils/nsf-membership.js";
import { TOUR_PENDING } from "../utils/tour-logic.js";
import {
    tournamentListIsComplete, assembledContentIsComplete, saveRetryDelayMs, isConnectionFailure
} from "../utils/offline-guards.js";

// syncDataFromFirestore writes the resolved role into the header. The element is
// looked up here rather than passed in because this module is evaluated at the
// same point in the page's life as firebase-auth.js, which holds the other
// references to the same header.
const userRoleLabel = document.getElementById('user-role-label');

// Dynamic fetch of all owned & shared tournaments.
//
// Returns the list plus whether it is COMPLETE. Offline, getDocs answers from the
// local cache instead of failing, so a list can come back short without any error;
// and a failed query comes back empty. Either way the caller may use what it got,
// but must not conclude that a tournament missing from it is gone
// (tournamentListIsComplete in js/utils/offline-guards.js).
async function fetchUserTournaments() {
    if (!currentUser) return { tournaments: [], complete: false };
    try {
        const tRef = collection(db, "tournaments");
        
        // Fetch tournaments where we are owner
        const qOwner = query(tRef, where("ownerId", "==", currentUser.uid));
        const ownerSnap = await getDocs(qOwner);
        let fromCache = ownerSnap.metadata.fromCache === true;
        const list = [];
        ownerSnap.forEach(document => {
            list.push({ id: document.id, ...document.data() });
        });

        // Fetch tournaments where we are cooperating arbiter (emails are stored lowercased)
        const qCoop = query(tRef, where("cooperatingArbiters", "array-contains", currentUser.email.trim().toLowerCase()));
        const coopSnap = await getDocs(qCoop);
        fromCache = fromCache || coopSnap.metadata.fromCache === true;
        coopSnap.forEach(document => {
            if (!list.some(t => t.id === document.id)) {
                list.push({ id: document.id, ...document.data() });
            }
        });

        return { tournaments: list, complete: tournamentListIsComplete({ failed: false, fromCache }) };
    } catch (e) {
        console.error("Error fetching global tournaments:", e);
        return { tournaments: [], complete: tournamentListIsComplete({ failed: true }) };
    }
}

/**
 * Make sure this account has a users/{uid} document, creating it from the token
 * if it does not.
 *
 * The role is copied from the CLAIM, which is the only trustworthy source: the
 * document is writable by its owner, so the copy stored there is a convenience
 * for display and for the server-side lookups that read it (resolveUserRole in
 * functions/lib/users.js), never a permission.
 *
 * An existing document is left alone. Someone whose role was changed by an
 * administrator gets the new claim immediately — adminApi revokes their refresh
 * tokens — and the document catches up the same way it always has.
 */
async function ensureUserDoc(user) {
    if (!user) return;
    try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);
        const existing = snap.exists() ? snap.data() : null;
        const hasRole = !!(existing && (existing.role || (existing.userProfile && existing.userProfile.role)));
        if (hasRole) return;

        await setDoc(ref, {
            email: (user.email || '').trim().toLowerCase(),
            // No claim means an account that predates them; 'organizer' is the
            // same default the rest of this file has always applied to those.
            role: currentClaims.role || 'organizer',
            createdAt: (existing && existing.createdAt) || new Date().toISOString(),
            // No document at all is a NEW account: its first confirmed sign-in.
            // It is offered the tour (js/ui/tour.js), once, whichever device it
            // signs in on next. A document that exists without a role is an old
            // account being repaired, and is left out.
            ...(existing ? {} : { userProfile: { tour: TOUR_PENDING } })
        }, { merge: true });
    } catch (e) {
        // Not fatal: the app reads the role from the token, and the next save
        // writes the document anyway.
        console.error('Could not create the user document:', e);
    }
}

// Sync Logic
async function syncDataFromFirestore(uid) {
    try {
        const docRef = doc(db, "users", uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();

            // Merge role into userProfile for easy access in script.js
            if (data.role) {
                // Migration: treat old 'organizer_player' as 'organizer'
                if (data.role === 'organizer_player') {
                    data.role = 'organizer';
                }
                data.userProfile = data.userProfile || {};
                data.userProfile.role = data.role;
            }
            // For older accounts without a role, default to organizer
            if (!data.userProfile || !data.userProfile.role) {
                data.userProfile = data.userProfile || {};
                data.userProfile.role = 'organizer';
            }


            // Merge shared and owned tournaments into the state history list
            const { tournaments: globalTournaments, complete: listComplete } = await fetchUserTournaments();
            const globalIds = new Set(globalTournaments.map(gt => String(gt.id)));

            // Drop cached SHARED tournaments (owned by someone else) that no longer exist remotely
            // — i.e. the owner deleted them, or we were removed as a cooperating arbiter.
            // Only on a list we know is complete: on bad Wi-Fi the list comes back
            // empty or cache-only, and "missing from it" then means nothing — every
            // shared tournament used to vanish from My Tournaments until the next
            // good sign-in. Entries the list did not refresh stay as the user doc's
            // stubs; app.js knows not to open a stub as it stands (isHistoryStub).
            const mergedHistory = (data.history || []).filter(h => {
                if (!listComplete) return true;
                const ownerId = h.data && h.data.ownerId;
                const isShared = ownerId && ownerId !== uid;
                return !(isShared && !globalIds.has(String(h.id)));
            });

            globalTournaments.forEach(gt => {
                const isV2 = (gt.schemaVersion || 1) >= 2;
                const roundsArray = Array.isArray(gt.rounds) ? gt.rounds : [];
                const roundsConfig = gt.roundsCount || gt.roundCount || (Array.isArray(gt.rounds) ? Math.max(gt.rounds.length, 1) : gt.rounds) || 7;
                const archivalData = {
                    id: gt.id,
                    name: gt.name,
                    date: gt.startDate,
                    format: gt.format,
                    // v2 meta docs carry denormalized counters instead of the arrays.
                    playerCount: isV2 ? (gt.playerCount || 0) : (gt.players || []).length,
                    roundCount: isV2 ? (gt.roundsPlayed || 0) : roundsArray.length,
                    data: { ...gt, rounds: roundsConfig },
                    players: gt.players || [],
                    rounds: roundsArray,
                    // Marks that the full data lives in the content subcollection and
                    // must be lazily fetched by openTournament (fetchFullTournament).
                    contentSplit: isV2
                };

                const existingIndex = mergedHistory.findIndex(h => String(h.id) === String(gt.id));
                if (existingIndex !== -1) {
                    mergedHistory[existingIndex] = archivalData;
                } else {
                    mergedHistory.push(archivalData);
                }
            });

            data.history = mergedHistory;

            // The stored role is the role. Nothing here second-guesses it.
            //
            // There used to be a "self-healing migration" at this point: an account
            // stored as 'player' that had any tournaments was rewritten to
            // 'organizer' in Firestore, on the theory that it was an arbiter whose
            // role had been corrupted. It was aimed at accounts predating the role
            // field — but those never reach it. An account with no `role` is already
            // defaulted to 'organizer' thirty lines above, so the heal could only
            // ever fire on a document that explicitly said 'player', i.e. on someone
            // who deliberately chose Player when they registered.
            //
            // What it actually did was make the leak in discardPreviousAccountData
            // permanent. Local data left behind by an arbiter reached the player
            // account's user document, the heal read it back as "they have
            // tournaments", and wrote role: 'organizer' — so the player account kept
            // the arbiter's sidebar on every future sign-in, on any device, long
            // after the local leak itself was fixed. Narrowing the evidence to
            // server-side ownership was not enough: a player who accepts a
            // co-arbiter invitation, or who created anything at all during the
            // window when they wrongly had the arbiter UI, still trips it forever.
            //
            // A heuristic that silently rewrites a user's role is not worth the one
            // case it was meant to catch. An arbiter who really is stored as a
            // player is now fixed by changing the field, not by guessing on every
            // sign-in.
            //
            // THE CLAIM WINS. Everything above is about the stored copy; what
            // an account may actually DO is decided by the `role` custom claim,
            // because firestore.rules reads that and nothing else. Drawing the
            // arbiter sidebar for someone the rules will refuse would be worse
            // than useless — every button on it would fail. An account with no
            // claim (created before they existed) falls back to the stored
            // value exactly as before.
            const resolvedRole = currentClaims.role
                || data.role || (data.userProfile && data.userProfile.role) || 'organizer';
            data.userProfile = data.userProfile || {};
            data.userProfile.role = resolvedRole;

            // The account signed out, or another tab signed somebody else in, while
            // this was on its way (firebase-auth.js, followAccountChange). Loaded
            // now, its data would land in a tab that has already wiped it.
            if (!currentUser || currentUser.uid !== uid) return;

            if (userRoleLabel) {
                userRoleLabel.textContent = resolvedRole === 'player' ? tr('Player') : tr('Arbiter');
            }

            if (window.app && window.app.loadFullState) {
                window.app.loadFullState(data);
            }
        }
    } catch (error) {
        console.error("Error syncing from Firestore:", error);
    }
}

// ===== Tournament schema v2 ==================================================
// tournaments/{id}                     — META ONLY (settings, owner/arbiters,
//                                        groups WITHOUT players/rounds,
//                                        denormalized counters, schemaVersion: 2)
// tournaments/{id}/content/players_{g} — { players: [...] }          per group
// tournaments/{id}/content/rounds_{g}  — INDEX { split, roundNos, structureRev }
// tournaments/{id}/content/rounds_{g}_r{n} — { round } one document PER ROUND
// {g} = group id, or 'main' for single-group tournaments.
//
// The per-round split is schema v3 (see js/utils/rounds-split.js for why and for
// the layout details). v2 wrote every round of a group into rounds_{g}; that doc
// is still READ transparently, and the first v3 save replaces it with the index
// form. Nothing above the storage layer changes: assembleTournament still hands
// out the same in-memory shape.
//
// Why: the legacy single-doc layout embedded every player and round (twice for
// multi-group tournaments) and would eventually hit Firestore's 1 MB doc cap,
// silently breaking saves — the same reason welcomePages got its own collection.
// Doc granularity also isolates player edits from result entry (multi-arbiter).
// Legacy (v1) docs are still readable everywhere (dual-read); the first v2 save
// of a v1 doc replaces it with the meta-only form automatically (setDoc replace).

// Build the per-group PLAYER docs for a tournament from its live data. Rounds
// are handled separately (writeGroupRounds) because the split layout needs
// per-round merging, index ownership and orphan cleanup.
//
// Each roster is stored in two halves (js/utils/player-privacy.js): the public
// list in content/players_{g}, which spectators read, and the e-mail, phone,
// birth date, registration answers and registration id of each player in
// private/players_{g}, which only the tournament's arbiters can read.
//   docId -> { public: { players }, private: { players }, rosterIds }
function buildPlayerDocs(data, players) {
    const docs = {};
    const add = (docId, list) => {
        const roster = Array.isArray(list) ? list : [];
        const { publicPlayers, privateEntries } = splitRoster(roster);
        docs[docId] = {
            public: { players: publicPlayers },
            private: { players: privateEntries },
            // The roster as it was stored before the split, for when the private
            // half cannot be (see writeContentDocs).
            full: { players: roster },
            rosterIds: roster.filter(p => p && p.id !== undefined && p.id !== null).map(p => String(p.id))
        };
    };
    if (data.groups && data.groups.length > 0) {
        data.groups.forEach(g => add('players_' + g.id, g.players || []));
    } else {
        add('players_main', players || data.players || []);
    }
    return docs;
}

// The player-doc ids a tournament's meta document implies — one per group, or
// 'players_main'. Used to record which private halves a successful read covered.
function playerDocIdsOf(meta) {
    return (meta && Array.isArray(meta.groups) && meta.groups.length > 0)
        ? meta.groups.map(g => 'players_' + g.id)
        : ['players_main'];
}

// One entry per group: the rounds to persist and the group's structure revision.
// 'main' is the single-group sentinel, matching the doc-id scheme.
function roundsUnits(data, rounds) {
    if (data.groups && data.groups.length > 0) {
        return data.groups.map(g => ({
            key: String(g.id),
            rounds: g.rounds || [],
            structureRev: g._structureRev || 0
        }));
    }
    return [{ key: 'main', rounds: rounds || [], structureRev: data._structureRev || 0 }];
}

// Reassemble the legacy in-memory shape from a v2 meta doc + its content docs so
// every consumer (spectator view, open-from-history, co-arbiter sync) keeps
// working with the shape it always had: top-level players/rounds arrays plus
// fully populated groups[].
//
// `privateMap` holds the private halves of the rosters (private/players_{g}),
// which only an arbiter can read; a spectator passes nothing and gets the public
// players alone.
function assembleTournament(meta, contentMap, privateMap) {
    if (!meta) return meta;
    if ((meta.schemaVersion || 1) < 2) return meta; // v1 doc: already assembled
    notePopulatedContent(meta.id, contentMap);
    const out = { ...meta };
    const privates = privateMap || {};
    const rosterOf = (docId) => mergeRoster(
        (contentMap[docId] || {}).players || [],
        (privates[docId] || {}).players || []
    );
    if (meta.groups && meta.groups.length > 0) {
        out.groups = meta.groups.map(g => {
            // readGroupRounds handles both the v3 split layout and the legacy v2
            // fat doc, and carries the authoritative structure revision (the meta
            // copy can lag behind a co-arbiter's content write).
            const rd = readGroupRounds(contentMap, String(g.id));
            return {
                ...g,
                players: rosterOf('players_' + g.id),
                rounds: rd.rounds,
                _structureRev: rd.structureRev || g._structureRev || 0
            };
        });
        const activeId = meta.activeGroupId || out.groups[0].id;
        const active = out.groups.find(g => String(g.id) === String(activeId)) || out.groups[0];
        out.players = active.players;
        out.rounds = active.rounds;
        out._structureRev = active._structureRev;
    } else {
        const rd = readGroupRounds(contentMap, 'main');
        out.players = rosterOf('players_main');
        out.rounds = rd.rounds;
        out._structureRev = rd.structureRev || meta._structureRev || 0;
    }
    return out;
}

// Merge two copies of a group's rounds (multi-arbiter conflict handling).
// The higher structureRev owns the round STRUCTURE — which rounds exist and
// which pairings they contain (structural edits bump it: generate round, undo
// round, edit pairings). Results merge per board INDEPENDENTLY of who owns the
// structure, keyed off each pairing's `resultTs` (stamped on every result
// write, including clearing back to "---" — an explicit clear is otherwise
// indistinguishable from "never entered" and would get silently overwritten
// by a stale non-empty copy). Whichever side stamped its result more recently
// wins for that board, so a fresh local edit — including clearing a result —
// is never discarded just because the remote echo of an earlier state hasn't
// caught up yet. Only when neither side carries a timestamp (older data) do
// we fall back to "adopt whichever side is non-empty, local wins a tie".
function mergeRounds(localRounds, localRev, remoteRounds, remoteRev) {
    localRounds = Array.isArray(localRounds) ? localRounds : [];
    remoteRounds = Array.isArray(remoteRounds) ? remoteRounds : [];
    localRev = localRev || 0;
    remoteRev = remoteRev || 0;

    const baseIsLocal = localRev >= remoteRev;
    const base = baseIsLocal ? localRounds : remoteRounds;
    const overlay = baseIsLocal ? remoteRounds : localRounds;

    // Index both sides' pairings by "roundNo:board index" so a board's result
    // can be looked up regardless of which side ended up as the structure base.
    const indexByKey = (rounds) => {
        const map = {};
        rounds.forEach(r => (r.pairings || []).forEach((p, idx) => { map[(r.roundNo || 0) + ':' + idx] = p; }));
        return map;
    };
    const localByKey = indexByKey(localRounds);
    const remoteByKey = indexByKey(remoteRounds);

    let adopted = 0;
    const rounds = base.map(round => {
        const oRound = overlay.find(r => (r.roundNo || 0) === (round.roundNo || 0));
        if (!oRound) return round;
        let touched = false;
        const pairings = (round.pairings || []).map((p, idx) => {
            const op = (oRound.pairings || [])[idx];
            if (!op || op.white !== p.white || op.black !== p.black) return p;

            const key = (round.roundNo || 0) + ':' + idx;
            const localP = (localByKey[key] && localByKey[key].white === p.white && localByKey[key].black === p.black) ? localByKey[key] : null;
            const remoteP = (remoteByKey[key] && remoteByKey[key].white === p.white && remoteByKey[key].black === p.black) ? remoteByKey[key] : null;
            const localResult = localP ? (localP.result || '') : '';
            const remoteResult = remoteP ? (remoteP.result || '') : '';
            if (localResult === remoteResult) return p;

            const localTs = (localP && localP.resultTs) || 0;
            const remoteTs = (remoteP && remoteP.resultTs) || 0;

            let winner, winnerTs;
            if (localTs || remoteTs) {
                // At least one side knows when it was written — the newer stamp wins,
                // even if that means adopting an explicit clear ('').
                const localWins = localTs >= remoteTs;
                winner = localWins ? localResult : remoteResult;
                winnerTs = localWins ? localTs : remoteTs;
            } else if (!localResult) {
                winner = remoteResult; winnerTs = remoteTs;   // only remote has it — adopt it
            } else if (!remoteResult) {
                winner = localResult; winnerTs = localTs;     // only local has it — adopt it
            } else {
                winner = localResult; winnerTs = localTs;     // true conflict, no timestamps — never erase local
            }

            if (winner === (p.result || '') && winnerTs === (p.resultTs || 0)) return p;
            touched = true;
            adopted++;
            return { ...p, result: winner, resultTs: winnerTs };
        });
        return touched ? { ...round, pairings } : round;
    });

    return { rounds, rev: Math.max(localRev, remoteRev), baseIsLocal, adopted };
}

// Full dual-read fetch: meta + content docs, assembled to the legacy shape.
// Used wherever the complete tournament is needed (spectator watch, lazy open).
//
// Null means "could not load", never "empty". Offline, getDocs answers from the
// local cache, and a cache that never held this tournament's content answers with
// no documents at all — which used to assemble into the right settings with no
// players and no rounds, open like that, and be saved back.
async function fetchFullTournament(id) {
    try {
        const meta = await fetchTournamentMeta(id);
        if (!meta) return null;
        if ((meta.schemaVersion || 1) < 2) return meta; // legacy doc carries everything
        const [contentSnap, privateMap] = await Promise.all([
            getDocs(collection(db, "tournaments", id, "content")),
            fetchPrivateRosters(id, meta)
        ]);
        const contentMap = {};
        contentSnap.forEach(d => { contentMap[d.id] = d.data(); });
        const assembled = assembleTournament(meta, contentMap, privateMap);
        if (!assembledContentIsComplete(meta, assembled, contentSnap.metadata.fromCache === true)) {
            console.warn('Tournament content is not fully available offline; not loading a partial copy:', id);
            return null;
        }
        return assembled;
    } catch (error) {
        console.error("Error fetching full tournament:", error);
        return null;
    }
}

// Is the signed-in account one of this tournament's arbiters (owner, co-arbiter,
// or invited)? Only they can read the tournament document itself, and only the
// first two the private halves of its rosters (firestore.rules).
function isArbiterOf(meta) {
    if (!currentUser || !meta) return false;
    if (meta.ownerId === currentUser.uid) return true;
    const email = (currentUser.email || '').trim().toLowerCase();
    return !!email && (meta.cooperatingArbiters || []).includes(email);
}

const isPermissionDenied = (e) => !!e && (e.code === 'permission-denied' || /insufficient permissions/i.test(e.message || ''));

// The tournament's meta document: the real one for its arbiters, the public
// mirror (publicTournaments/{id}, written by the mirrorPublicTournament function)
// for everyone else. The real document carries the arbiters' addresses, the
// registration access code and the scan token, so the rules let nobody else read
// it; the mirror is the same document with those taken out
// (functions/lib/public-tournament.js). Null when neither can be read: the
// tournament does not exist, or is not public and this is not its arbiter.
async function fetchTournamentMeta(id) {
    if (currentUser) {
        try {
            const snap = await getDoc(doc(db, "tournaments", id));
            if (snap.exists()) return snap.data();
        } catch (e) {
            if (!isPermissionDenied(e)) throw e;
            // Refused: not this account's tournament. Spectate it like anyone else.
        }
    }
    const pub = await getDoc(doc(db, "publicTournaments", id));
    return pub.exists() ? pub.data() : null;
}

// The private halves of a tournament's rosters (private/players_{g}), for its
// owner and co-arbiters; {} for anyone else, or when they cannot be read. A
// successful read from the SERVER is recorded per document (privateHeld below):
// it is what entitles this device to replace those documents wholesale later.
async function fetchPrivateRosters(id, meta) {
    if (!isArbiterOf(meta)) return {};
    try {
        const snap = await getDocs(collection(db, "tournaments", id, "private"));
        const map = {};
        snap.forEach(d => { map[d.id] = d.data(); });
        if (!snap.metadata.fromCache) notePrivateHeld(id, meta);
        return map;
    } catch (e) {
        console.warn('Could not read the private player details; showing the public roster only:', id, e);
        return {};
    }
}

// Private roster documents this device has READ from the server (or written) in
// this session. Only for those does a save write its own copy outright; for any
// other it first folds in what the cloud holds (writePrivatePlayers), so a device
// whose read failed can never blank out everyone's contact details.
const privateHeld = new Set();

function privatePath(tournamentId, docId) {
    return tournamentId + '/private/' + docId;
}

// A server read of the private collection covers every roster the meta implies,
// including the ones with no document: those genuinely hold nothing private.
function notePrivateHeld(tournamentId, meta) {
    playerDocIdsOf(meta).forEach(docId => privateHeld.add(privatePath(tournamentId, docId)));
}

// What writePrivatePlayers returns when the rules will not let this account read or
// write the private half at all (see writeContentDocs for what the caller does).
const PRIVATE_REFUSED = 'refused';

// Write the private half of one roster (private/players_{g}). Returns true once it
// is written (or unchanged), false when it has to be left for the retry, and
// PRIVATE_REFUSED when the rules refuse it.
//
// A device that has HELD the document (read it from the server, or created the
// tournament) writes its own copy outright. Any other device — its read failed, or
// it opened the tournament before the split existed — folds in what the cloud holds
// for the players still on its roster first, so it can add and correct entries but
// never wipe the ones it never received. That merge is repeated on every changed
// save until a real read makes the device a holder: marking it a holder after a
// merge would let its next save drop the entries it only borrowed.
async function writePrivatePlayers(tournamentId, docId, parts, mergeWithRemote) {
    const path = privatePath(tournamentId, docId);
    const serialized = stableStringify(parts.private);
    if (lastWrittenHash.get(path) === serialized) return true;

    let payload = parts.private;
    const held = !mergeWithRemote || privateHeld.has(path);
    if (!held) {
        try {
            const snap = await getDoc(doc(db, "tournaments", tournamentId, "private", docId));
            if (snap.metadata.fromCache) return false;
            const remote = snap.exists() ? (snap.data().players || []) : [];
            payload = { players: mergePrivateEntries(remote, parts.private.players, parts.rosterIds) };
        } catch (e) {
            if (isConnectionFailure(e)) return false;
            // Refused outright: the rules give this account no private half here.
            console.warn('Private player details could not be read; keeping them on the roster:', path, e);
            return PRIVATE_REFUSED;
        }
    }
    try {
        await setDoc(doc(db, "tournaments", tournamentId, "private", docId), payload);
    } catch (e) {
        if (!isPermissionDenied(e)) throw e;
        console.warn('Private player details could not be written; keeping them on the roster:', path, e);
        return PRIVATE_REFUSED;
    }
    lastWrittenHash.set(path, serialized);
    if (held) privateHeld.add(path);
    return true;
}

// Per-doc change-skip: remember the last serialized payload written per doc path
// so unchanged docs are skipped. A result-entry burst then writes only the active
// group's rounds doc + the small meta doc instead of every doc every time.
// Serialized with stableStringify, NOT JSON.stringify — see js/utils/stable-json.js:
// key order is not stable across a sync round-trip, and plain stringify made every
// unchanged document look changed.
const lastWrittenHash = new Map();

// Content docs this client has genuinely HELD POPULATED at some point in this
// session — because the cloud handed us one with players in it, or because we
// ourselves wrote one.
//
// This is the signal the empty-payload guard below was missing. An empty roster
// arrives in two completely different situations that look identical on the
// wire: the arbiter deliberately removed the last player, or this client never
// got the content at all (a co-arbiter who opened the tournament before it
// loaded, a failed fetch). Refusing both meant a real removal never reached the
// cloud — the arbiter watched the player vanish from their own list while every
// spectator went on seeing them, because the spectator view reads these very
// documents. Refusing neither would reopen the data-loss hole.
//
// Having once held the document populated separates them exactly: the client
// that never received content is never in here, so it stays blocked.
const populatedContentSeen = new Set();

function contentPath(tournamentId, docId) {
    return tournamentId + '/' + docId;
}

// Remember which player docs came back populated, so a later emptying of one of
// them reads as an edit rather than as an uninitialised client.
function notePopulatedContent(tournamentId, contentMap) {
    if (!tournamentId || !contentMap) return;
    Object.entries(contentMap).forEach(([docId, payload]) => {
        if (!docId.startsWith('players_')) return;
        const players = payload && payload.players;
        if (Array.isArray(players) && players.length > 0) {
            populatedContentSeen.add(contentPath(tournamentId, docId));
        }
    });
}

// Last known state of a group's rounds INDEX doc, keyed `tournamentId/groupKey`:
// { exists, roundNos, structureRev, fetchedAt }. Lets a result-entry save skip
// the index read (see writeGroupRounds); every index write refreshes it.
const roundsIndexCache = new Map();
const ROUNDS_INDEX_TTL_MS = 60000;

// Cache of the meta-doc fields writeTournamentDoc needs, keyed by tournament id:
// { exists, ownerId, ownerEmail, ownerName, cooperatingArbiters,
//   pendingCooperatingArbiters, fetchedAt }.
// Why: writeTournamentDoc used to getDoc the meta doc on EVERY save — and saves
// fire on a 2.5s throttle during result entry — just to learn (a) whether the
// parent exists (write ordering) and (b) the owner/arbiter fields to preserve
// when the local copy lacks them. Caching eliminates that read-per-save. The
// 60s TTL bounds the staleness of the arbiter-list fallbacks, which only matter
// when the local copy is missing those fields; every successful meta write
// refreshes the entry with the values just written.
const metaDocCache = new Map();
const META_CACHE_TTL_MS = 60000;

// When each tournament's meta doc was last written, keyed by tournament id.
// Result-only saves skip the meta write entirely (see writeTournamentDoc); this
// drives the slow heartbeat that still refreshes `lastUpdated` often enough for
// the public-tournament list to order sensibly.
const metaWriteAt = new Map();
const META_HEARTBEAT_MS = 10 * 60 * 1000;

// Persist ONE group's rounds in the v3 split layout (js/utils/rounds-split.js):
// a small document per round plus an index document naming which rounds exist.
//
// The point of the split is the hash skip below: during result entry only the
// round actually being scored has a changed payload, so a save writes one small
// document instead of rewriting (and re-pushing to every spectator) all of them.
// A save that changes nothing costs zero reads and zero writes.
//
// Returns false when the group could not be written SAFELY — a read the merge
// depends on failed (bad Wi-Fi) — so the caller reports the save as failed and it
// is retried. Nothing is guessed in that case: nothing written for the group is
// left for readers to follow (the index goes last and is not touched).
async function writeGroupRounds(tournamentId, unit, mergeWithRemote) {
    const { docs, roundNos: localNos } = buildRoundDocs(unit.key, unit.rounds, unit.structureRev);
    const indexId = roundsIndexDocId(unit.key);
    const localRev = unit.structureRev || 0;
    const hashOf = (docId) => lastWrittenHash.get(tournamentId + '/' + docId);
    const serializedOf = {};
    Object.entries(docs).forEach(([docId, payload]) => { serializedOf[docId] = stableStringify(payload); });

    // Nothing in this group's rounds changed — no reads, no writes at all.
    if (Object.keys(docs).every(docId => hashOf(docId) === serializedOf[docId])) return true;

    // Read the index to learn what the cloud currently believes the structure is.
    // The read is SKIPPED while our own index payload is unchanged — i.e. during
    // result entry, which is the overwhelmingly common save. That is safe because
    // an unchanged index is also never written back, so a stale cached view cannot
    // clobber a co-arbiter's structure; it only supplies remoteRev to the per-round
    // merge. Any structural edit (generate/undo round) re-reads for real.
    const cacheKey = tournamentId + '/' + unit.key;
    const cached = roundsIndexCache.get(cacheKey);
    const indexUnchanged = hashOf(indexId) === serializedOf[indexId];
    const canUseCache = mergeWithRemote && indexUnchanged && cached &&
                        (Date.now() - cached.fetchedAt) < ROUNDS_INDEX_TTL_MS;

    let remoteNos = [], remoteRev = 0, hasRemote = false, legacyRounds = null;
    if (canUseCache) {
        remoteNos = cached.roundNos;
        remoteRev = cached.structureRev;
        hasRemote = cached.exists;
    } else if (mergeWithRemote) {
        try {
            const snap = await getDoc(doc(db, "tournaments", tournamentId, "content", indexId));
            if (snap.exists()) {
                const index = snap.data();
                hasRemote = true;
                remoteRev = index.structureRev || 0;
                if (index.split) {
                    remoteNos = Array.isArray(index.roundNos) ? index.roundNos : [];
                } else {
                    // LEGACY v2 fat doc: its array IS the remote copy and no per-round
                    // documents exist yet, so every round merges against it and this
                    // save replaces the doc with the index form. That is the migration.
                    legacyRounds = Array.isArray(index.rounds) ? index.rounds : [];
                    remoteNos = legacyRounds.map((r, i) => roundNoOf(r, i));
                }
            }
            roundsIndexCache.set(cacheKey, {
                exists: hasRemote, roundNos: remoteNos, structureRev: remoteRev, fetchedAt: Date.now()
            });
        } catch (e) {
            // Not knowing what the cloud holds is not the same as it holding
            // nothing. Offline, this used to fall through as "no remote", which let
            // an empty or partial local copy replace the index — and readers follow
            // the index, so the rounds were gone for everyone. Write nothing; the
            // save is retried once the connection is back. (Any other failure
            // keeps its old meaning: treated as no remote copy.)
            if (isConnectionFailure(e)) {
                console.warn('Could not read the rounds index; leaving the rounds of this group for the retry:', tournamentId, unit.key);
                return false;
            }
        }
    }

    const plan = planRoundsWrite({ localNos, localRev, remoteNos, remoteRev, hasRemote });
    if (plan.skip) {
        console.warn('Refusing to overwrite populated rounds with empty payload:', tournamentId, unit.key);
        Object.keys(docs).forEach(docId => lastWrittenHash.delete(tournamentId + '/' + docId));
        return true;
    }

    // 1. Per-round documents. Only rounds we actually hold locally are written —
    //    if the remote structure won and it carries a round we've never seen, its
    //    document is already there and must be left alone.
    for (const no of plan.finalNos) {
        const docId = roundDocId(unit.key, no);
        const payload = docs[docId];
        if (!payload) continue;
        const path = tournamentId + '/' + docId;
        if (hashOf(docId) === serializedOf[docId]) continue;

        let toWrite = payload;
        if (mergeWithRemote) {
            let remoteRound = null;
            if (legacyRounds) {
                remoteRound = legacyRounds.find((r, i) => roundNoOf(r, i) === no) || null;
            } else if (remoteNos.indexOf(no) !== -1) {
                try {
                    const rs = await getDoc(doc(db, "tournaments", tournamentId, "content", docId));
                    if (rs.exists()) remoteRound = rs.data().round || null;
                } catch (e) {
                    // Offline there is no remote round to merge a co-arbiter's
                    // results from, and writing ours over it would erase them. Stop
                    // before the index, which is what readers follow.
                    if (isConnectionFailure(e)) {
                        console.warn('Could not read a round to merge; leaving it for the retry:', tournamentId, docId);
                        return false;
                    }
                }
            }
            if (remoteRound) {
                // Same per-board result merge as before, scoped to this round, so a
                // save still can't erase a result a co-arbiter entered meanwhile.
                const merged = mergeRounds([payload.round], localRev, [remoteRound], remoteRev);
                if (merged.rounds[0]) toWrite = { round: merged.rounds[0] };
            }
        }
        await setDoc(doc(db, "tournaments", tournamentId, "content", docId), toWrite);
        lastWrittenHash.set(path, serializedOf[docId]);
    }

    // 2. Remove rounds that no longer exist (undo round, or the remote structure
    //    won and dropped one). Best-effort: an orphan is harmless because readers
    //    follow the index, never the document set.
    for (const no of plan.toDelete) {
        const docId = roundDocId(unit.key, no);
        try {
            await deleteDoc(doc(db, "tournaments", tournamentId, "content", docId));
        } catch (e) {
            console.warn('Could not delete stale round doc:', docId, e);
        }
        lastWrittenHash.delete(tournamentId + '/' + docId);
    }

    // 3. Index LAST: it is what readers follow, so it must never point at a round
    //    document that hasn't been written yet.
    const finalIndex = {
        split: true,
        roundNos: plan.finalNos,
        structureRev: Math.max(localRev, remoteRev)
    };
    const indexPath = tournamentId + '/' + indexId;
    const indexSerialized = stableStringify(finalIndex);
    if (lastWrittenHash.get(indexPath) !== indexSerialized) {
        await setDoc(doc(db, "tournaments", tournamentId, "content", indexId), finalIndex);
        lastWrittenHash.set(indexPath, indexSerialized);
    }
    // The write just made these values authoritative (same reasoning as
    // metaDocCache): later result-entry saves can trust them without a read.
    roundsIndexCache.set(cacheKey, {
        exists: true, roundNos: finalIndex.roundNos, structureRev: finalIndex.structureRev, fetchedAt: Date.now()
    });
    return true;
}

// Write a tournament in schema v2: content docs first, meta LAST (its lastUpdated
// acts as the commit marker for listeners). Reads the existing meta doc only to
// preserve owner/arbiter metadata that may not be on the local copy — and only
// when the cached copy of that metadata is missing or older than 60s. Shared by
// the routine save and the explicit import path.
//
// Throws (after writing whatever it safely could) when a read it depends on failed,
// so the save is reported as failed and retried rather than completed on a guess.
// `isNew` is for a caller that knows the id has never been written — an import,
// which always mints a fresh one — and so has nothing to read first.
async function writeTournamentDoc(data, { players, rounds, isLive, isNew = false }) {
    if (!currentUser || !data || !data.id) return;
    const tourneyRef = doc(db, "tournaments", data.id);

    let existingDoc = {};
    let parentExists = false;
    // Whether existingDoc came from a getDoc in THIS call (vs the TTL cache).
    // The arbiter lists below may only be taken from a genuinely fresh read.
    let readWasFresh = false;
    const cached = metaDocCache.get(data.id);
    if (isNew) {
        // Nothing to read: a fresh id cannot have a document behind it.
    } else if (cached && (Date.now() - cached.fetchedAt) < META_CACHE_TTL_MS) {
        // Fresh cache entry: skip the per-save getDoc entirely.
        parentExists = cached.exists;
        if (cached.exists) {
            existingDoc = {
                ownerId: cached.ownerId,
                ownerEmail: cached.ownerEmail,
                ownerName: cached.ownerName,
                cooperatingArbiters: cached.cooperatingArbiters,
                pendingCooperatingArbiters: cached.pendingCooperatingArbiters
            };
        }
    } else {
        let snap = null;
        let offline = false;
        try {
            snap = await getDoc(tourneyRef);
        } catch (e) {
            // Refused by the rules is how a tournament that does not exist yet
            // looks (they read the owner off the document), and it keeps meaning
            // "new" below. Only a connection failure leaves the answer unknown.
            offline = isConnectionFailure(e);
        }
        // Not knowing is not "new". Taken as new, the content was written with no
        // merge and no empty-roster guard (a co-arbiter's results overwritten, a
        // roster blanked), and the arbiter lists came from this device's stale copy
        // — undoing an accepted invite. The offline cache saying "no such document"
        // is not knowing either. Stop here; the save is retried once the cloud can
        // be asked.
        if (offline || (snap && !snap.exists() && snap.metadata.fromCache)) {
            throw new Error('Could not confirm the tournament in the cloud (offline?); the save will be retried.');
        }
        if (snap) {
            if (snap.exists()) { existingDoc = snap.data(); parentExists = true; }
            // Fresh only if the SERVER answered: offline, getDoc hands back the
            // cached copy, whose arbiter lists are as stale as any other.
            readWasFresh = !snap.metadata.fromCache;
            metaDocCache.set(data.id, {
                exists: parentExists,
                ownerId: existingDoc.ownerId,
                ownerEmail: existingDoc.ownerEmail,
                ownerName: existingDoc.ownerName,
                cooperatingArbiters: existingDoc.cooperatingArbiters,
                pendingCooperatingArbiters: existingDoc.pendingCooperatingArbiters,
                fetchedAt: Date.now()
            });
        }
    }

    // Content docs (skip the ones whose payload didn't change). Ordering matters:
    // for an EXISTING tournament, content first and meta last (meta.lastUpdated is
    // the commit marker for listeners). For a NEW tournament the meta doc must go
    // first — the content security rules check get(parent), which would deny the
    // content writes while the parent doc doesn't exist yet.
    // Set when a document had to be left unwritten because a read it depends on
    // failed; the whole save then reports failure at the end so it is retried.
    let incomplete = false;
    const writeContentDocs = async (mergeWithRemote) => {
        const playerDocs = buildPlayerDocs(data, players);
        for (const [docId, parts] of Object.entries(playerDocs)) {
            const path = data.id + '/' + docId;
            const newIsEmpty = !Array.isArray(parts.public.players) || parts.public.players.length === 0;
            if (lastWrittenHash.get(path) !== stableStringify(parts.public)) {
                // For an existing tournament an empty payload makes us consult the
                // remote copy before refusing to wipe populated data — UNLESS we have
                // held this very document populated already (see populatedContentSeen),
                // which means the emptiness is an edit we made and not content we never
                // received. Removing the last player of a group is the ordinary way to
                // get here, and it has to reach the cloud: the spectator view is built
                // from these documents, so a refused write leaves everyone but the
                // arbiter still looking at a player who left.
                if (mustVerifyBeforeEmptying({
                    mergeWithRemote,
                    newIsEmpty,
                    heldPopulated: populatedContentSeen.has(path)
                })) {
                    try {
                        const remoteSnap = await getDoc(doc(db, "tournaments", data.id, "content", docId));
                        if (remoteSnap.exists()) {
                            const remoteArr = remoteSnap.data().players;
                            // SAFETY INVARIANT (shared-tournament data-loss guard):
                            // never overwrite populated cloud content with an empty
                            // array. A client that hasn't loaded a tournament — e.g. a
                            // co-arbiter who just accepted an invite and opened it before
                            // its content arrived, or whose fetch failed — must not be
                            // able to blank out everyone's players. Such a client has
                            // never seen this document populated, so it never reaches
                            // the exemption above. We skip the write AND drop the hash
                            // so a later populated save still goes.
                            if (Array.isArray(remoteArr) && remoteArr.length > 0) {
                                console.warn('Refusing to overwrite populated content with empty payload:', path);
                                lastWrittenHash.delete(path);
                                continue;
                            }
                        }
                    } catch (e) {
                        // Offline, the check could not be made — which is exactly when
                        // this client is most likely the one that never received the
                        // roster. It used to write the empty list anyway. Leave it for
                        // the retry. (Any other failure keeps its old meaning.)
                        if (isConnectionFailure(e)) {
                            console.warn('Could not verify before emptying a roster; leaving it for the retry:', path);
                            lastWrittenHash.delete(path);
                            incomplete = true;
                            continue;
                        }
                    }
                }
            }

            // The private half goes FIRST, because whether it could be stored decides
            // what the public half may leave out. A roster the guard above refused
            // has `continue`d past this, so its private half is left alone with it.
            //
            // Stored: the public roster goes out without the private fields. Left for
            // the retry: so is the public half — it would only drop fields that are
            // not safe anywhere yet. REFUSED by the rules — this app running against
            // rules from before the split, for the minutes of a deploy, or rules
            // rolled back — and the public roster keeps the fields, exactly as it did
            // before the split, rather than drop the only copy of everyone's contact
            // details. scripts/migrate-private-player-fields.mjs moves them later.
            const stored = await writePrivatePlayers(data.id, docId, parts, mergeWithRemote);
            if (stored === false) {
                incomplete = true;
                continue;
            }
            const payload = stored === PRIVATE_REFUSED ? parts.full : parts.public;
            const serialized = stableStringify(payload);
            if (lastWrittenHash.get(path) === serialized) continue;
            await setDoc(doc(db, "tournaments", data.id, "content", docId), payload);
            // Hash the LOCAL payload (not the merged one): if local state hasn't
            // changed since, the next save has nothing new to contribute and can
            // skip the read+write entirely.
            lastWrittenHash.set(path, serialized);
            // A roster we wrote with players in it is one we demonstrably hold, so
            // a later save that empties it is an edit — same reasoning as the load
            // path above, for the case where we populated the document ourselves.
            if (!newIsEmpty) populatedContentSeen.add(path);
        }

        for (const unit of roundsUnits(data, rounds)) {
            if (!await writeGroupRounds(data.id, unit, mergeWithRemote)) incomplete = true;
        }
    };
    if (parentExists) await writeContentDocs(true);

    // Meta doc — strip the heavy arrays; groups keep settings only. `rounds`
    // stays the CONFIG number (round count), as legacy views read it.
    const roundsArr = rounds || (Array.isArray(data.rounds) ? data.rounds : []);
    const roundsCount = parseInt(data.roundsCount || (Array.isArray(data.rounds) ? Math.max(data.rounds.length, 1) : data.rounds)) || 7;
    // How many leading rounds a spectator may see, per group and for the
    // tournament as a whole. Derived rather than stored on the tournament,
    // because the truth is the `published` flag on each round — but the rounds
    // themselves live in the content subcollection, and the ONE reader that
    // cannot go and look is the broadcast ticker
    // (functions/lib/broadcast-handlers.js), which already holds this document
    // and must not put a round's boards on the public feed before its pairings
    // are out. Meaningless while `pairingPublish` is 'auto', where it is simply
    // the round count.
    const groupsMeta = (data.groups && data.groups.length > 0)
        ? data.groups.map(g => {
            const { players: _p, rounds: _r, ...settings } = g;
            return { ...settings, publishedRounds: publishedRoundCount(g.rounds) };
        })
        : null;
    const playerCount = (data.groups && data.groups.length > 0)
        ? data.groups.reduce((n, g) => n + ((g.players || []).length), 0)
        : (players || data.players || []).length;
    const roundsPlayed = (data.groups && data.groups.length > 0)
        ? data.groups.reduce((n, g) => Math.max(n, (g.rounds || []).length), 0)
        : roundsArr.length;

    // `lastUpdated` is stripped from the incoming data too: it rides along on an
    // assembled tournament, and leaving it in would make the payload look changed
    // on every save and defeat the skip below.
    // The two arbiter lists are deliberately stripped from the local copy here and
    // re-attached from the LIVE doc just before the write (see below). They are
    // owned by the invite / accept / decline / retract / remove flows, which
    // mutate the live document with arrayUnion/arrayRemove; this device's
    // in-memory copy is only ever a snapshot of them and must never be written
    // back. Doing so used to revert an invitee's acceptance: the owner's next meta
    // write (any settings change, a new round, or just the 10-minute heartbeat)
    // put the invitee back into `pending` and dropped them from `cooperating`,
    // which is why an accepted invite still showed as pending for the owner and
    // the tournament never appeared for the co-arbiter.
    const {
        players: _players, rounds: _rounds, groups: _groups, lastUpdated: _lu,
        cooperatingArbiters: _coop, pendingCooperatingArbiters: _pending,
        ...metaRest
    } = data;
    // Everything except the timestamp and the arbiter lists — this is what decides
    // whether a write is actually needed. Keeping the lists out also means a stale
    // local copy of them can no longer TRIGGER a write of its own.
    const metaBody = {
        ...metaRest,
        ...(groupsMeta ? { groups: groupsMeta } : {}),
        rounds: roundsCount,
        roundsCount: roundsCount,
        playerCount,
        roundsPlayed,
        publishedRounds: publishedRoundCount(roundsArr),
        schemaVersion: 2,
        ownerId: data.ownerId || existingDoc.ownerId || currentUser.uid,
        ownerEmail: data.ownerEmail || existingDoc.ownerEmail || currentUser.email,
        ownerName: data.ownerName || existingDoc.ownerName || currentUser.displayName || currentUser.email.split('@')[0],
        isLive: !!isLive,
        // Which NSF series the groups belong to ('ngp', 'bgp'): what the NSF console looks
        // NGP and BGP tournaments up by (functions/lib/nsf-console-api.js), since a query
        // cannot reach into the groups.
        nsfSeriesInUse: nsfSeriesInUse(data)
    };

    // Skip the meta write when only results changed. The meta doc is ~1.5 KB and
    // was previously rewritten on EVERY save purely to refresh `lastUpdated`, so
    // once rounds were split it became ~70% of what each save pushes to every
    // spectator — and a second document read for each of them. Nothing depends on
    // the timestamp advancing: the sync comparison in app.js explicitly strips
    // `lastUpdated` before diffing, and the listener fires on content changes on
    // its own. Its one real consumer is the public-tournament list ordering
    // (searchPublicTournaments), which a slow heartbeat keeps accurate enough.
    //
    // A NEW tournament always writes: the meta doc must exist before the content
    // security rules will allow the content writes.
    const metaPath = data.id + '/__meta';
    const metaHash = stableStringify(metaBody);
    const metaUnchanged = lastWrittenHash.get(metaPath) === metaHash;
    const heartbeatDue = (Date.now() - (metaWriteAt.get(data.id) || 0)) >= META_HEARTBEAT_MS;

    if (parentExists && metaUnchanged && !heartbeatDue) {
        // Refresh the field cache anyway: nothing changed, so what it holds is
        // still authoritative and a later save can keep skipping the getDoc.
        const prev = metaDocCache.get(data.id);
        if (prev) metaDocCache.set(data.id, { ...prev, fetchedAt: Date.now() });
        if (incomplete) throw new Error(INCOMPLETE_WRITE_MESSAGE);
        return;
    }

    // Re-attach the arbiter lists from the live document. This is a full-replace
    // setDoc, so they have to be carried through — but they must come from the
    // doc as it stands RIGHT NOW, not from this device's copy and not from the
    // TTL cache (which only echoes what we ourselves last wrote). One extra read,
    // and only on a save that actually rewrites the meta doc — result-only saves
    // return above. It is also what lets a CO-ARBITER save at all: the rules
    // require their write to carry both lists through exactly unchanged.
    if (parentExists && !readWasFresh) {
        let listsFresh;
        try {
            const fresh = await getDoc(tourneyRef);
            if (fresh.exists()) existingDoc = fresh.data();
            listsFresh = !fresh.metadata.fromCache;
        } catch (e) {
            // Only a connection failure means "could not read"; anything else
            // falls through with the lists we hold, as it always did.
            listsFresh = !isConnectionFailure(e);
        }
        if (!listsFresh) {
            // Offline, or answered from the cache. Writing the lists we have would
            // put back whatever they were when we last saw them — undoing an
            // invitation accepted since, which is the very revert this read exists
            // to prevent. Leave the meta document for the retry, which comes as
            // soon as the connection does; the content above is already written.
            console.warn('Arbiter lists could not be read from the server; leaving the tournament settings for the retry:', data.id);
            throw new Error(INCOMPLETE_WRITE_MESSAGE);
        }
    }
    const metaDoc = {
        ...metaBody,
        cooperatingArbiters: existingDoc.cooperatingArbiters || (parentExists ? [] : (data.cooperatingArbiters || [])),
        pendingCooperatingArbiters: existingDoc.pendingCooperatingArbiters || (parentExists ? [] : (data.pendingCooperatingArbiters || [])),
        lastUpdated: new Date().toISOString()
    };
    await setDoc(tourneyRef, metaDoc); // full replace: first v2 save sheds a v1 doc's embedded arrays
    lastWrittenHash.set(metaPath, metaHash);
    metaWriteAt.set(data.id, Date.now());

    // The write just made these values authoritative — cache them so follow-up
    // saves within the TTL need no getDoc at all (the doc now certainly exists).
    metaDocCache.set(data.id, {
        exists: true,
        ownerId: metaDoc.ownerId,
        ownerEmail: metaDoc.ownerEmail,
        ownerName: metaDoc.ownerName,
        cooperatingArbiters: metaDoc.cooperatingArbiters,
        pendingCooperatingArbiters: metaDoc.pendingCooperatingArbiters,
        fetchedAt: Date.now()
    });

    if (!parentExists) await writeContentDocs(false); // new tournament: content after meta (see above)
    if (incomplete) throw new Error(INCOMPLETE_WRITE_MESSAGE);
}

const INCOMPLETE_WRITE_MESSAGE = 'Some tournament documents could not be checked against the cloud (offline?); they will be written on the retry.';

// Returns true only if every write succeeded — the throttle uses this to decide
// whether the persistent dirty flag may be cleared.
async function saveDataToFirestore(state) {
    if (!currentUser) return false;
    let ok = true;
    try {
        // Strip the role fields from state before saving to prevent overwriting/corrupting the role in Firestore.
        // Also strip the heavy tournament data: the user doc references the active
        // tournament by ID only (schema v2) — its data lives in tournaments/{id} +
        // content subcollection, killing the user doc's own 1 MB exposure.
        const { userProfile, activeTournament: _at, players: _pl, rounds: _ro, ...otherState } = state;
        const profileToSave = { ...userProfile };
        if (profileToSave) {
            delete profileToSave.role;
        }

        // Store history as lightweight metadata only. The heavy players/rounds are
        // the redundant part: on reload syncDataFromFirestore rebuilds history from
        // the `tournaments` collection (fetchUserTournaments) and overwrites this.
        // We keep data.ownerId so the "drop stale shared tournament" filter still works.
        // Coalesce every field to null — a `undefined` here would make setDoc reject
        // the whole user-doc write.
        const lightHistory = (state.history || []).map(h => {
            const hd = h.data || {};
            return {
                id: h.id ?? null,
                name: h.name ?? null,
                date: h.date ?? null,
                format: h.format ?? null,
                playerCount: h.playerCount ?? null,
                roundCount: h.roundCount ?? null,
                data: {
                    id: hd.id ?? h.id ?? null,
                    ownerId: hd.ownerId ?? null,
                    isPublic: hd.isPublic ?? null,
                    name: hd.name ?? h.name ?? null
                }
            };
        });

        // Write the active tournament FIRST and independently — it is the doc that
        // reload-rebuild and spectators rely on, so a problem writing the user doc
        // must never block it (and vice-versa).
        if (state.activeTournament) {
            try {
                await writeTournamentDoc(state.activeTournament, {
                    players: state.players || [],
                    rounds: state.rounds || [],
                    isLive: true
                });
            } catch (e) {
                ok = false;
                console.error("Error saving active tournament to Firestore:", e);
            }
        }

        try {
            const docRef = doc(db, "users", currentUser.uid);
            await setDoc(docRef, {
                ...otherState,
                activeTournamentId: state.activeTournament ? state.activeTournament.id : null,
                // The user doc is written with merge:true, so the legacy embedded
                // copies from docs saved before the slimming would linger forever —
                // shed them explicitly.
                activeTournament: deleteField(),
                players: deleteField(),
                rounds: deleteField(),
                history: lightHistory,
                userProfile: profileToSave,
                email: currentUser.email.trim().toLowerCase()
            }, { merge: true });
        } catch (e) {
            ok = false;
            console.error("Error saving user doc to Firestore:", e);
        }

        // Archived tournaments are never edited unless they are the active one
        // (then the write above covers them), so we no longer re-read/re-write each
        // one here, and we no longer run a full-collection delete scan. Deletion is
        // handled explicitly by deleteTournamentAndNotify; imports write their own
        // doc via writeTournamentDoc.

    } catch (error) {
        ok = false;
        console.error("Error saving to Firestore:", error);
    }
    return ok;
}

// --- Throttled network sync --------------------------------------------------
// saveToLocalStorage() fires on every mutation (36 call sites). localStorage stays
// synchronous; the Firestore sync is throttled with a LEADING edge:
//   - the FIRST change writes immediately (an isolated edit has zero delay window),
//   - follow-up changes within THROTTLE_MS coalesce into ONE trailing write.
// A plain trailing debounce is forbidden here — it caused a production data-loss
// incident (refresh within the window lost the write; unload flushes don't
// complete; the stale cloud then clobbered localStorage on reload).
//
// Safety net: a persistent dirty flag (cloudSyncPending in localStorage) is set
// synchronously whenever a sync is owed and cleared only after a CONFIRMED
// successful write with no newer pending changes. If the tab dies before the
// trailing write, loadFullState sees the flag on next load, keeps the local
// data, and re-pushes it — the missed write self-heals. Unload flushes below are
// a best-effort optimization, not a correctness mechanism.
let saveTimer = null;
let pendingState = null;
let inFlightPromise = null;
let lastSaveStart = 0;
let saveGeneration = 0;
const THROTTLE_MS = 2500;
const DIRTY_KEY = 'cloudSyncPending';
// Fired once a save has landed with nothing newer waiting, so the live listener can
// hand on the cloud copy it held back meanwhile (hasUnsavedLocalChanges below, and
// js/services/arbiters.js) — a co-arbiter's change that arrived during the save is
// applied then, not lost.
const SAVE_SETTLED_EVENT = 'zw:cloud-save-settled';

// The state object the app last asked to have saved — the live state, which is
// always the same object — kept so the 'online' listener has something to save
// when the dirty flag was left by a save whose retries have run out.
let lastScheduledState = null;

function scheduleSaveToFirestore(state) {
    if (!currentUser) return;
    pendingState = state;
    lastScheduledState = state;
    saveGeneration++;
    try { localStorage.setItem(DIRTY_KEY, '1'); } catch (e) {}
    if (saveTimer || inFlightPromise) return; // an armed timer / trailing rerun picks it up
    const elapsed = Date.now() - lastSaveStart;
    const delay = elapsed >= THROTTLE_MS ? 0 : THROTTLE_MS - elapsed;
    saveTimer = setTimeout(runScheduledSave, delay);
}

// Consecutive failed saves, for the retry backoff (saveRetryDelayMs).
let failedSaveAttempts = 0;

async function runScheduledSave() {
    saveTimer = null;
    if (!pendingState || !currentUser) return;
    const s = pendingState;
    pendingState = null;
    const gen = saveGeneration;
    lastSaveStart = Date.now();
    inFlightPromise = saveDataToFirestore(s);
    const ok = await inFlightPromise;
    inFlightPromise = null;
    // Clear the dirty flag only if the write succeeded AND nothing newer arrived meanwhile.
    if (ok && gen === saveGeneration) {
        try { localStorage.removeItem(DIRTY_KEY); } catch (e) {}
    }
    failedSaveAttempts = ok ? 0 : failedSaveAttempts + 1;
    if (ok && !pendingState) window.dispatchEvent(new Event(SAVE_SETTLED_EVENT));
    // Changes made while the write was in flight get their own trailing write.
    if (pendingState && !saveTimer) {
        saveTimer = setTimeout(runScheduledSave, THROTTLE_MS);
    } else if (!ok && !saveTimer) {
        // A save that failed — typically bad Wi-Fi, now that the sync refuses to
        // write on a guess — used to wait for the next edit or the next page load.
        // An arbiter who enters the last results and walks away would leave them
        // on this device only. Try again on a backoff; the 'online' listener below
        // cuts the wait short when the connection comes back.
        const delay = saveRetryDelayMs(failedSaveAttempts);
        if (delay !== null) {
            pendingState = s;
            saveTimer = setTimeout(runScheduledSave, delay);
        }
    }
}

// Whether this device holds edits the cloud has not confirmed: a save is queued
// (the throttle window, or a retry waiting out its backoff) or the last one failed.
// The live listener's handler (js/app.js) reads it so that a snapshot arriving in
// that window — on bad Wi-Fi that window is the whole retry wait — cannot replace
// the local settings and roster with the older cloud copy. Rounds still merge; that
// merge is per board and already safe.
//
// A save IN FLIGHT counts too. It writes one document at a time — a roster's private
// half first, then its public half, then the settings — and each one the server
// confirms reaches the listener on its own. Taken as it came, the private half's
// confirmation assembled the new private half with the OLD public roster, so a player
// just deleted came back on the list and vanished again a moment later.
function hasUnsavedLocalChanges() {
    return !!pendingState || !!inFlightPromise || failedSaveAttempts > 0;
}

// The connection is back: anything still owed to the cloud goes now rather than
// at the end of a backoff. The dirty flag is what says something is owed, so this
// also covers a save whose retries had run out.
window.addEventListener('online', () => {
    let owed = false;
    try { owed = localStorage.getItem(DIRTY_KEY) === '1'; } catch (e) {}
    if (!owed || !currentUser || inFlightPromise) return;
    if (!pendingState) pendingState = lastScheduledState;
    if (!pendingState) return;
    failedSaveAttempts = 0;
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(runScheduledSave, 0);
});

async function flushPendingSave() {
    if (saveTimer) {
        clearTimeout(saveTimer);
        saveTimer = null;
    }
    if (inFlightPromise) await inFlightPromise; // wait out the current write
    if (pendingState) await runScheduledSave(); // then write anything newer
}

// The opposite of a flush, for deleting a profile: throw the queued state away
// instead of writing it. A save that landed after the user doc was deleted would
// recreate it — the write is a setDoc(merge:true), which creates what it cannot
// find. The in-flight write is awaited rather than abandoned so that it lands
// BEFORE the delete rather than after it, and the dirty flag goes too, or the
// next load would see unsynced work owed by an account that no longer exists.
async function cancelPendingSave() {
    if (saveTimer) {
        clearTimeout(saveTimer);
        saveTimer = null;
    }
    pendingState = null;
    if (inFlightPromise) await inFlightPromise;
    pendingState = null; // the awaited write may have queued a trailing one
    if (saveTimer) {
        clearTimeout(saveTimer);
        saveTimer = null;
    }
    try { localStorage.removeItem(DIRTY_KEY); } catch (e) { /* ignore */ }
}

// Best-effort flush when the tab is hidden or closed (the dirty flag covers the
// cases where these writes don't complete).
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushPendingSave();
});
window.addEventListener('pagehide', flushPendingSave);

// Global Search for Spectators
// Which of the three public buckets a tournament falls into, from its dates alone.
//
// A started tournament with no end date counts as still running rather than
// finished — an organizer who never filled in an end date has not thereby
// declared the event over, and the alternative (calling it finished the moment
// it starts) would drop live events off the Ongoing tab. Same reasoning for a
// tournament with no dates at all.
//
// `today` is an ISO date string; the stored dates are ISO too, so the plain
// string comparisons order correctly without parsing.
function classifyTournamentStatus(data, today) {
    if (!data.startDate) return 'ongoing';
    if (data.endDate) {
        if (data.startDate <= today && data.endDate >= today) return 'ongoing';
        return data.endDate < today ? 'finished' : 'upcoming';
    }
    return data.startDate <= today ? 'ongoing' : 'upcoming';
}

// The public lists — Ongoing, Upcoming, Finished — from the spectators' mirror
// (publicTournaments, see fetchTournamentMeta). Two ordered queries rather than
// one: every tournament that has not ended yet, and the most recently finished.
// It used to be a single `isPublic == true, limit(100)` with no order, which past
// a hundred public tournaments would have handed back an arbitrary hundred and
// let a tournament being played today drop off Ongoing. Both queries order on one
// field, so Firestore's automatic indexes serve them.
const CURRENT_LIMIT = 300;
const FINISHED_LIMIT = 100;

async function searchPublicTournaments(searchTerm = '') {
    try {
        const tRef = collection(db, "publicTournaments");
        // Norway's date, not UTC's: just after midnight it is already tomorrow here.
        const today = osloDate();

        const [current, finished] = await Promise.all([
            getDocs(query(tRef, where("listUntil", ">=", today), orderBy("listUntil"), limit(CURRENT_LIMIT))),
            getDocs(query(tRef, where("listUntil", "<", today), orderBy("listUntil", "desc"), limit(FINISHED_LIMIT)))
        ]);
        let results = [];
        const term = searchTerm.toLowerCase();

        [current, finished].forEach(snap => snap.forEach((doc) => {
            const data = doc.data();

            const matchesSearch = !term || (data.name && data.name.toLowerCase().includes(term));
            if (!matchesSearch) return;

            results.push({ id: doc.id, ...data, status: classifyTournamentStatus(data, today) });
        }));

        // Sort by last updated or start date
        results.sort((a, b) => new Date(b.lastUpdated || b.startDate) - new Date(a.lastUpdated || a.startDate));

        return results;
    } catch (error) {
        console.error("Error searching tournaments:", error);
        return [];
    }
}

// One tournament's meta document — the arbiters' own, or the public mirror for
// anyone else (fetchTournamentMeta).
async function getTournamentById(id) {
    try {
        return await fetchTournamentMeta(id);
    } catch (error) {
        console.error("Error fetching tournament:", error);
    }
    return null;
}

// Drop every cached trace of a tournament. Called by arbiters.js the moment it
// finishes deleting one: a later save — of a re-imported tournament with the same
// id, say — must not trust exists=true, skip a content write as "unchanged", or
// reuse a stale round index. A function rather than four exported Maps, so the
// caches stay this module's own business.
function forgetTournamentCaches(tournamentId) {
    metaDocCache.delete(tournamentId);
    metaWriteAt.delete(tournamentId);
    const prefix = tournamentId + '/';
    [lastWrittenHash, roundsIndexCache].forEach(cache => {
        Array.from(cache.keys()).forEach(key => { if (key.indexOf(prefix) === 0) cache.delete(key); });
    });
    Array.from(privateHeld).forEach(key => { if (key.indexOf(prefix) === 0) privateHeld.delete(key); });
}

export {
    fetchUserTournaments,
    ensureUserDoc,
    syncDataFromFirestore,
    assembleTournament,
    mergeRounds,
    fetchFullTournament,
    hasUnsavedLocalChanges,
    writeTournamentDoc,
    saveDataToFirestore,
    scheduleSaveToFirestore,
    flushPendingSave,
    cancelPendingSave,
    forgetTournamentCaches,
    notePrivateHeld,
    classifyTournamentStatus,
    searchPublicTournaments,
    getTournamentById
};
